/**
 * StatTile.
 *
 * Compact KPI tile: label on top in caption-strong, big number below.
 * Used in the stat-tile strip on list/dashboard pages.
 *
 * Optional `tone` paints just the number; backgrounds stay neutral so a
 * strip of tiles doesn't shout.
 *
 * Patch P13 — accepts an optional `onClick` so the tile can act as a
 * click-to-filter chip (used by the Forecast page to filter by risk
 * level). When `onClick` is set, the tile renders as a button with hover
 * feedback and supports an `active` boolean for the selected state.
 */
import type { MouseEventHandler, ReactNode } from 'react';
import { cn } from '@/lib/cn';

export type StatTileTone = 'neutral' | 'success' | 'warning' | 'danger';

export interface StatTileProps {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: StatTileTone;
  className?: string;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  active?: boolean;
}

const toneText: Record<StatTileTone, string> = {
  neutral: 'text-primary',
  success: 'text-status-success',
  warning: 'text-status-warning',
  danger: 'text-status-danger',
};

const toneActiveRing: Record<StatTileTone, string> = {
  neutral: 'ring-2 ring-accent',
  success: 'ring-2 ring-status-success',
  warning: 'ring-2 ring-status-warning',
  danger: 'ring-2 ring-status-danger',
};

export function StatTile({
  label,
  value,
  hint,
  tone = 'neutral',
  className,
  onClick,
  active,
}: StatTileProps) {
  const inner = (
    <>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-tertiary">{label}</div>
      <div className={cn('mt-1 text-2xl font-semibold tabular-nums', toneText[tone])}>{value}</div>
      {hint ? <div className="mt-0.5 text-xs text-secondary">{hint}</div> : null}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={active}
        className={cn(
          'rounded-md border border-border-default bg-surface px-4 py-3 text-left transition-shadow hover:border-border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
          active && toneActiveRing[tone],
          className,
        )}
      >
        {inner}
      </button>
    );
  }
  return (
    <div className={cn('rounded-md border border-border-default bg-surface px-4 py-3', className)}>
      {inner}
    </div>
  );
}
